<template>
  <Tray :open="dirty" wrap>
    <div class="flex h-8 items-center">
      <span class="pl-1">You have unsaved changes</span>
    </div>

    <div class="flex grow items-center justify-end gap-2">
      <TrayAction
        v-if="onReset"
        icon="arrow-uturn-left"
        label="Discard"
        description="Discard changes"
        hotkey="r"
        @click="onReset"
      />

      <TrayAction
        icon="check"
        label="Save"
        description="Save changes"
        hotkey="s"
        variant="primary"
        :pending="saveMutation.isPending.value"
        :disabled="saveMutation.isPending.value || issues.length > 0"
        :allow-clicking-despite-disablement="
          !saveMutation.isPending.value && issues.length > 0
        "
        :tooltip-delay-duration="issues.length > 0 ? 0 : 1000"
        @click="handleSave"
      >
        <template v-if="issues.length > 0" #content>
          <ul class="flex flex-col gap-2">
            <li v-for="(issue, idx) in issues" :key="idx">
              {{ issue.message }}
            </li>
          </ul>
        </template>
      </TrayAction>
    </div>
  </Tray>
</template>

<script setup lang="ts">
import { useMutation } from "@tanstack/vue-query";
import { nextTick, ref } from "vue";

import { OkapiError } from "@/lib/openapi";
import { showToast } from "@/lib/toasts";

import TrayAction from "./Action.vue";
import Tray from "./Root.vue";

export interface Issue {
  message: string;
  selector?: string;
}

const props = withDefaults(
  defineProps<{
    dirty?: boolean;
    issues?: Issue[];
    onSave: () => void | Promise<void>;
    onReset?: () => void;
  }>(),
  {
    dirty: false,
    issues: () => [],
  }
);

const shakeCount = ref(0);

const saveMutation = useMutation({
  async mutationFn() {
    return props.onSave();
  },
  onError(err) {
    showToast({
      type: "error",
      title: "Failed to save changes",
      message:
        err instanceof OkapiError ? err.message : "Please try again later.",
    });
  },
});

const handleSave = () => {
  const issues = props.issues;

  if (issues.length > 0) {
    const selector = issues[0].selector;

    if (selector) {
      const element = document.querySelector(selector);

      if (element) {
        const rect = element.getBoundingClientRect();

        // If the element is not in view, scroll until it is.
        if (!rect.top || rect.top < 0 || rect.bottom > window.innerHeight) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }

        shakeCount.value = Math.min(shakeCount.value + 1, 3);
        element.classList.remove(
          ...Array.from(
            { length: shakeCount.value },
            (_, i) => `shaking-${i + 1}`
          )
        );

        nextTick(() => {
          element.classList.add(`shaking-${shakeCount.value}`);
        });
      }
    }

    return;
  }

  shakeCount.value = 0;
  saveMutation.mutate();
};
</script>
